import { useState } from 'react'
import MemoryUsedNote from '../shared/MemoryUsedNote'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { workoutPlannerAPI } from '../../services/api'
import { PlanGeneratorForm } from './PlanGeneratorForm'
import { PlanReview } from './PlanReview'
import { buildGenerateRequest, buildAcceptRequest, nextIsoWeek, parseAcceptError, planWeeks } from './planHelpers'

export function AIPlanGeneratorDialog({ onClose, onAccepted }) {
  const { t } = useTranslation()
  const start = nextIsoWeek()
  // Inputs survive a failed generation so the user can retry.
  const [options, setOptions] = useState({
    fitness_goals: ['build_muscle'],
    fitness_level: 'intermediate',
    days_per_week: 4,
    session_duration_minutes: 60,
    injuries_limitations: '',
    use_analysis: true,
    block_weeks: 4,
    start_year: start.year,
    start_week: start.week,
  })
  const [plan, setPlan] = useState(null)
  const [weeks, setWeeks] = useState([])
  const [acceptError, setAcceptError] = useState(null)
  const [saved, setSaved] = useState(null)

  const generate = useMutation({
    mutationFn: () => workoutPlannerAPI.generateWorkoutPlan(buildGenerateRequest(options)),
    onSuccess: (res) => {
      setPlan(res.data)
      setWeeks(planWeeks(res.data))
      setAcceptError(null)
    },
  })

  const accept = useMutation({
    mutationFn: () =>
      workoutPlannerAPI.acceptWorkoutPlan(
        buildAcceptRequest(weeks, { year: options.start_year, week: options.start_week })
      ),
    onSuccess: (res) => {
      setSaved(res.data.programs)
      onAccepted?.(res.data.programs)
    },
    onError: (err) => setAcceptError(parseAcceptError(err)),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="ai-plan-title">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 id="ai-plan-title" className="text-xl font-bold text-gray-900">
            {plan ? t('aiPlan.review.title') : t('aiPlan.title')}
          </h2>
          <button type="button" onClick={onClose} aria-label={t('aiPlan.close')} className="text-gray-400">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {saved ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-800">{t('aiPlan.saved', { n: saved.length })}</p>
            <ul className="list-disc pl-5 text-sm text-gray-700">
              {saved.map((p) => (
                <li key={p.program_id}>
                  {t('aiPlan.savedWeek', { week: p.week_number, isoWeek: p.iso_week, year: p.year, sessions: p.session_count })}
                  {p.is_deload && ` · ${t('aiPlan.review.deload')}`}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button type="button" className="btn-primary" onClick={onClose}>{t('aiPlan.close')}</button>
            </div>
          </div>
        ) : plan ? (
          <>
          <MemoryUsedNote memories={plan.memories_used} className="mb-3" />
          <PlanReview
            plan={plan}
            weeks={weeks}
            onWeeksChange={(next) => { setWeeks(next); setAcceptError(null) }}
            acceptError={acceptError}
            isAccepting={accept.isPending}
            onAccept={() => accept.mutate()}
            onBack={() => { setPlan(null); setAcceptError(null) }}
          />
          </>
        ) : (
          <PlanGeneratorForm
            options={options}
            onChange={setOptions}
            onSubmit={() => generate.mutate()}
            isGenerating={generate.isPending}
            error={generate.isError}
          />
        )}
      </div>
    </div>
  )
}
